import React from "react";
import PropTypes from "prop-types";
import { Box } from "theme-ui";
import { colors, typography } from "../../styles/theme";

const InfoTable = ({
  headers,
  striped,
  headingCells,
  rows,
  showEmptyHeader = false,
  labelWidth = "32%",
  customStyles,
}) => {
  const showHeaders = Array.isArray(headers) && headers.length > 0;

  return (
    <Box sx={{ ...styles.wrapper, ...customStyles }}>
      <Box
        as="table"
        sx={{
          ...styles.table,
          th: {
            ...styles.th,
            backgroundColor: striped ? colors.white : colors.secondaryBlue100,
            ...(headingCells
              ? { ...typography.h5, color: colors.neutral900 }
              : typography.textMd500),
          },
          ...(striped ? styles.stripedRows : {}),
          "td:first-of-type": {
            width: labelWidth,
            ...(headingCells
              ? { ...typography.h5, color: colors.black }
              : { ...typography.textMd400 }),
          },
        }}
      >
        {(showHeaders || showEmptyHeader) && (
          <thead>
            <tr>
              {showHeaders ? (
                headers.map((h, i) => (
                  <th key={`llm-column-${i}`}>{h}</th>
                ))
              ) : (
                <>
                  <th key="llm-column-0" aria-hidden="true" />
                  <th key="llm-column-1" aria-hidden="true" />
                </>
              )}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => {
            const cells = Array.isArray(row.cells)
              ? row.cells
              : [row.label, row.value];

            return (
              <tr key={`llm-row-${i}`}>
                {cells.map((cell, cellIndex) => (
                  <td
                    key={`llm-column-${cellIndex}`}
                    sx={
                      cellIndex === 0 && headingCells
                        ? styles.headingCell
                        : styles.bodyCell(cellIndex === 0)
                    }
                  >
                    {Array.isArray(cell) ? (
                      <Box as="ul" sx={styles.list}>
                        {cell.map((item, itemIndex) => (
                          <Box
                            as="li"
                            key={`llm-item-${i}-${itemIndex}`}
                            sx={styles.listItem}
                          >
                            {item}
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      cell
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </Box>
    </Box>
  );
};

const styles = {
  wrapper: {
    my: 4,
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    borderRadius: "0.5rem",
    border: `0.75px solid ${colors.neutral200}`,
  },
  table: {
    width: "100%",
    minWidth: ["36rem", "36rem", "100%"],
    borderCollapse: "collapse",
    ...typography.textMd400,
    "th, td": {
      padding: ["0.75rem 1rem", "1.25rem 1rem"],
      border: `1px solid ${colors.neutral200}`,
      textAlign: "left",
      verticalAlign: "top",
      color: colors.neutral900,
    },
    a: {
      color: colors.primary500,
      textDecoration: "none",
    },
  },
  th: {},
  stripedRows: {
    "tbody tr:nth-of-type(odd)": {
      backgroundColor: colors.secondaryBlue100,
    },
    "tbody tr:nth-of-type(even)": {
      backgroundColor: colors.white,
    },
  },
  headingCell: {
    ...typography.h5,
    color: colors.black,
  },
  bodyCell: (isFirst) => ({
    fontWeight: isFirst ? 600 : 400,
  }),
  list: {
    m: 0,
    pl: "1.25rem",
    listStyle: "disc",
  },
  listItem: {
    mb: "0.25rem",
  },
};

InfoTable.propTypes = {
  headers: PropTypes.arrayOf(PropTypes.string),
  striped: PropTypes.bool,
  headingCells: PropTypes.bool,
  showEmptyHeader: PropTypes.bool,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node,
      value: PropTypes.node,
      cells: PropTypes.arrayOf(PropTypes.node),
    }),
  ).isRequired,
  labelWidth: PropTypes.string,
  customStyles: PropTypes.object,
};

export default InfoTable;